import React from "react";
import { List, Set } from "immutable";

import PromiseButton from "../../../components/lib/PromiseButton";
import AdminContractorBulkEditor, {
  PARAMS_FOR_BULK_UPDATE,
} from "../components/AdminContractorBulkEditor";
import { emptyList } from "../../../constants";
import { usePLIContext } from "../context";
import { useProgramContext } from "../ProgramDataProvider";
import { bulkUpdateCheckDataToImmutableMap } from "../dataConverters";
import { getBulkUpdateFiltersQuery } from "../utils";

import type { ButtonProps } from "../../../components/lib/Button";
import type {
  FiltersQueryObject,
  FiltersQueryOrderedMap,
} from "../../../components/tables/types";
import type { PARAMS_FOR_BULK_UPDATE_TYPE } from "../components/AdminContractorBulkEditor";
import type { FetchAPIFunc, FetchAPIResponse } from "../../../types/fetch";
import type { ImmutableSet, ImmutableList } from "../../../types/immutable";
import type { CONTRACTOR_STATUSES_TYPE } from "../types";
import type { BulkUpdateCheckResultObject } from "../pages/StoredIndexCreate";

export type BulkUpdateContractorsButtonProps = Omit<ButtonProps, "onClick"> & {
  contractorsFiltersQuery: FiltersQueryOrderedMap;
  allowedStatusesForUpdate: ImmutableSet<CONTRACTOR_STATUSES_TYPE>;
  allowedParametersForUpdate: ImmutableSet<PARAMS_FOR_BULK_UPDATE_TYPE>;
  selectedIds?: ImmutableList<number>;
  onUpdateIsDone: (withProcessing?: boolean) => Promise<any>;
};

export async function checkBulkUpdateInBatches(
  fetchFilteringAPI: FetchAPIFunc,
  programId: number,
  filtersQuery: FiltersQueryObject
): Promise<FetchAPIResponse<BulkUpdateCheckResultObject>> {
  const path = `programs/${programId}/contractors/check_for_bulk_update/`;
  const { id__in: selectedIds, ...otherFilters } = filtersQuery;
  const otherFieldCount = Object.values(otherFilters).reduce<number>(
    (count, value) =>
      count + (Array.isArray(value) ? value.length : value === undefined ? 0 : 1),
    0
  );
  const batchSize = Math.min(500, 999 - otherFieldCount);
  if (batchSize < 1) throw new Error("Too many filter fields for a bulk-update request.");
  if (!Array.isArray(selectedIds) || selectedIds.length <= batchSize) {
    return fetchFilteringAPI(path, { data: filtersQuery });
  }

  const ids = Array.from(new globalThis.Set<number>(selectedIds));
  const batches: Array<FetchAPIResponse<BulkUpdateCheckResultObject>> = [];
  let eligibleIds = Set<number>();
  const distinctFields = {
    unique_titles_count: { field: "job_title", values: Set<string | number>() },
    unique_countries_count: {
      field: "processed__country_id",
      values: Set<string | number>(),
    },
    unique_industries_count: {
      field: "processed__industry_id",
      values: Set<string | number>(),
    },
  };

  for (let offset = 0; offset < ids.length; offset += batchSize) {
    const batchIds = ids.slice(offset, offset + batchSize);
    const batchQuery = { ...otherFilters, id__in: batchIds };
    const response = await fetchFilteringAPI(path, { data: batchQuery });
    const returnedIds = response.data?.ids_to_update;
    if (
      !Array.isArray(returnedIds) ||
      !returnedIds.every((id) => Number.isInteger(id) && batchIds.includes(id))
    ) {
      throw new Error("Invalid selected IDs in bulk-update response.");
    }
    if (
      ![
        response.data.region_based_data_count,
        response.data.location_based_data_count,
      ].every(
        (count) => Number.isInteger(count) && count >= 0 && count <= returnedIds.length
      ) ||
      typeof response.data.not_approved_data_found !== "boolean"
    ) {
      throw new Error("Invalid bulk-update eligibility metadata.");
    }
    batches.push(response);
    eligibleIds = eligibleIds.union(returnedIds);
    if (!returnedIds.length) continue;

    for (const entry of Object.values(distinctFields)) {
      const valuesResponse = await fetchFilteringAPI(
        `programs/${programId}/contractors/values/filtered/`,
        {
          params: { page: 1, page_size: 500 },
          data: { ...batchQuery, id__in: returnedIds, __resultset: [entry.field] },
        }
      );
      const values = valuesResponse.data?.results;
      if (
        !Array.isArray(values) ||
        valuesResponse.data.next ||
        valuesResponse.data.count !== values.length ||
        !values.every((value) => typeof value === "string" || typeof value === "number")
      ) {
        throw new Error("Incomplete bulk-update filter metadata.");
      }
      entry.values = entry.values.union(values);
    }
  }

  const first = batches[0];
  return {
    ...first,
    data: {
      ...first.data,
      ids_to_update: List(
        ids.filter((id) => eligibleIds.has(id))
      ) as unknown as ImmutableList<number>,
      unique_titles_count: distinctFields.unique_titles_count.values.size,
      unique_countries_count: distinctFields.unique_countries_count.values.size,
      unique_industries_count: distinctFields.unique_industries_count.values.size,
      region_based_data_count: batches.reduce(
        (count, batch) => count + batch.data.region_based_data_count,
        0
      ),
      location_based_data_count: batches.reduce(
        (count, batch) => count + batch.data.location_based_data_count,
        0
      ),
      not_approved_data_found: batches.some(
        (batch) => batch.data.not_approved_data_found
      ),
      worker_type_country_id:
        distinctFields.unique_countries_count.values.size === 1
          ? (batches.find((batch) => batch.data.worker_type_country_id != null) || first)
              .data.worker_type_country_id
          : (undefined as unknown as number),
      worker_type_industry_id:
        distinctFields.unique_industries_count.values.size === 1
          ? (batches.find((batch) => batch.data.worker_type_industry_id != null) || first)
              .data.worker_type_industry_id
          : (undefined as unknown as number),
    },
  };
}

export function BulkUpdateContractorsButton(props: BulkUpdateContractorsButtonProps) {
  const {
    contractorsFiltersQuery,
    allowedStatusesForUpdate,
    allowedParametersForUpdate,
    onUpdateIsDone,
    selectedIds,
    ...restProps
  } = props;
  const {
    store,
    isPTAdmin,
    userEmail,
    showConfirmationModal,
    closeConfirmationModal,
    showModalSuccess,
    showModalWarning,
    showModalError,
    fetchM8API,
    fetchM8FilteringAPI,
    fetchCCCV1API,
    fetchTasteAPI,
    fetchGraphQL,
    services,
  } = usePLIContext();
  const { programId } = useProgramContext();

  // handlers

  const handlePostBulkUpdateActions = React.useCallback(async () => {
    await onUpdateIsDone?.();
    await closeConfirmationModal();
  }, [onUpdateIsDone, closeConfirmationModal]);

  const handleBulkUpdateMappings = React.useCallback(async () => {
    if (!isPTAdmin) {
      showModalWarning("You have no permissions to update data mappings");
      return;
    }

    const filtersQuery: FiltersQueryObject = getBulkUpdateFiltersQuery(
      contractorsFiltersQuery,
      allowedStatusesForUpdate,
      selectedIds
    );
    if (
      (selectedIds != null && !selectedIds.size) ||
      (Array.isArray(filtersQuery.status__in) && !filtersQuery.status__in.length) ||
      (Array.isArray(filtersQuery.id__in) && !filtersQuery.id__in.length)
    ) {
      showModalWarning("No eligible rows are selected for bulk update.");
      return;
    }
    let response: FetchAPIResponse<BulkUpdateCheckResultObject>;
    try {
      response = await checkBulkUpdateInBatches(
        fetchM8FilteringAPI,
        programId,
        filtersQuery
      );
    } catch {
      showModalError(
        "Unable to check all selected rows. No updates were submitted. Please try again."
      );
      return;
    }
    const data = bulkUpdateCheckDataToImmutableMap(response.data);

    if (data && data.size) {
      let idsToUpdate = data.get("ids_to_update") || emptyList;
      const uniqueTitlesCount = data.get("unique_titles_count");
      const regionBasedDataCount = data.get("region_based_data_count");
      const locationBasedDataCount = data.get("location_based_data_count");
      const allowApproveAction = data.get("not_approved_data_found", false);
      const uniqueCountriesCount = data.get("unique_countries_count");
      const uniqueIndustriesCount = data.get("unique_industries_count");
      // if worker type is allowed for update - following data will be returned from API
      const workerTypeCountryId = data.get("worker_type_country_id");
      const workerTypeIndustryId = data.get("worker_type_industry_id");

      let paramsToUpdate = Set([
        PARAMS_FOR_BULK_UPDATE.INDUSTRY,
        PARAMS_FOR_BULK_UPDATE.EXP_LEVEL,
        // IMPORTANT: Per Marc - need more manual control on Accepted flag, should be always available
        PARAMS_FOR_BULK_UPDATE.LIMITS_ACCEPTED,
        PARAMS_FOR_BULK_UPDATE.GLOBAL_SUPPLIER_SEARCH,
      ]) as any as ImmutableSet<string>;

      // add those parameters available under certain conditions
      if (uniqueTitlesCount != null && uniqueTitlesCount === 1) {
        paramsToUpdate = paramsToUpdate
          .add(PARAMS_FOR_BULK_UPDATE.COLLECTION)
          .add(PARAMS_FOR_BULK_UPDATE.DESCRIPTION);
      }
      if (regionBasedDataCount != null && regionBasedDataCount === 0) {
        paramsToUpdate = paramsToUpdate.add(PARAMS_FOR_BULK_UPDATE.LOCATION);
      }
      if (locationBasedDataCount != null && locationBasedDataCount === 0) {
        paramsToUpdate = paramsToUpdate.add(PARAMS_FOR_BULK_UPDATE.REGION);
      }
      if (
        uniqueCountriesCount === 1 &&
        uniqueIndustriesCount === 1 &&
        workerTypeCountryId != null &&
        workerTypeIndustryId != null
      ) {
        paramsToUpdate = paramsToUpdate.add(PARAMS_FOR_BULK_UPDATE.WORKER_TYPE);
      }
      // IMPORTANT: Per Marc - need more manual control on Accepted flag, should be always available
      // if (!notVisibleDataFound && !emptyMarketDataFound &&
      //         !(globalSupplierDataFound && notGlobalSupplierDataFound)) {
      //   paramsToUpdate = paramsToUpdate.add(PARAMS_FOR_BULK_UPDATE.LIMITS_ACCEPTED);
      // }

      // filter out not allowed parameters
      paramsToUpdate = paramsToUpdate.intersect(allowedParametersForUpdate);

      if (selectedIds != null) {
        idsToUpdate = idsToUpdate.filter((id) => selectedIds.includes(id));
      }
      if (!idsToUpdate.size) {
        showModalWarning("No eligible rows are selected for bulk update.");
        return;
      }

      const header = "Bulk Update Contractors";
      const message = (
        <AdminContractorBulkEditor
          store={store}
          email={userEmail}
          programId={programId}
          idsToUpdate={idsToUpdate}
          paramsToUpdate={paramsToUpdate}
          allowApproveAction={allowApproveAction}
          workerTypeCountryId={workerTypeCountryId}
          workerTypeIndustryId={workerTypeIndustryId}
          onApply={handlePostBulkUpdateActions}
          onCancel={closeConfirmationModal}
          showModalSuccess={showModalSuccess}
          showModalError={showModalError}
          fetchM8API={fetchM8API}
          fetchCCCV1API={fetchCCCV1API}
          fetchTasteAPI={fetchTasteAPI}
          fetchGraphQL={fetchGraphQL}
          services={services}
        />
      );

      await showConfirmationModal(message, header);
    } else {
      await showModalError("Bad response format.");
    }
    return data;
  }, [
    store,
    selectedIds,
    programId,
    userEmail,
    isPTAdmin,
    contractorsFiltersQuery,
    allowedStatusesForUpdate,
    allowedParametersForUpdate,
    handlePostBulkUpdateActions,
    showConfirmationModal,
    closeConfirmationModal,
    showModalWarning,
    showModalSuccess,
    showModalError,
    fetchM8API,
    fetchM8FilteringAPI,
    fetchTasteAPI,
    fetchCCCV1API,
    fetchGraphQL,
    services,
  ]);

  return <PromiseButton onClick={handleBulkUpdateMappings} {...restProps} />;
}

export default BulkUpdateContractorsButton;
